import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../../../components/ui/Modal';
import { useLockBodyScroll } from '../common/common_utils/useLockBodyScroll';
import ownershipBuildingIcon from '../../../images/ownership-building.png';
import ownershipPersonIcon from '../../../images/ownership-person.png';
import '../css/OwnershipStructureChoice.css';

// Illustrations adapted from the supplied ownership-structure_1.html.
function Node({ person, name, role }) {
  return <span className="kycStructureNode">
    <span className="kycStructureIcon"><img src={person ? ownershipPersonIcon : ownershipBuildingIcon} alt="" draggable={false} /></span>
    <span className="kycStructureName">{name}</span>
    <span className="kycStructureRole">{role}</span>
  </span>;
}
function Link() {
  return <span className="kycStructureLink" aria-hidden="true">
    <svg viewBox="0 0 24 110" preserveAspectRatio="none">
      <line x1="12" y1="108" x2="12" y2="14" stroke="#1f2430" strokeWidth="2" />
      <polygon points="12,2 5,16 19,16" fill="#1f2430" />
    </svg>
    <span>100 %</span>
  </span>;
}

export default function OwnershipStructureChoice({ value, onChange, disabled }) {
  return <div className="kycStructureChoices" role="group" aria-label="Ownership structure">
    {[
      { value: 'direct', title: 'Direct shareholding', hint: 'Individuals hold shares directly in the client.' },
      { value: 'complex', title: 'Multi-layer shareholding', hint: 'Ownership passes through one or more entities.' },
    ].map(option => <button
      key={option.value}
      type="button"
      className={`kycStructureChoice kycStructureChoice--${option.value}${value === option.value ? ' is-active' : ''}`}
      aria-label={option.title}
      aria-pressed={value === option.value}
      disabled={disabled}
      onClick={() => onChange(option.value)}
    >
      <span className="kycStructureHeader">
        <strong>{option.title}</strong>
        <span>{option.hint}</span>
      </span>
      <span className="kycStructureIllustration" aria-hidden="true">
        <Node name="ACME Ltd" role="Client" />
        <Link />
        {option.value === 'complex' && <><Node name="Contoso Ltd" role="Intermediary company" /><Link /></>}
        <Node person name="John Doe" role={option.value === 'direct' ? 'Direct shareholder' : 'Indirect shareholder'} />
      </span>
    </button>)}
  </div>;
}

export function OwnershipStructureChoiceModal({ open, onClose, ...choiceProps }) {
  const cardRef = useRef(null);
  useLockBodyScroll(open, { strategy: 'overflow' });
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    cardRef.current?.querySelector('.kycStructureChoice')?.focus();
    const trapFocus = event => {
      if (event.key !== 'Tab') return;
      const buttons = [...(cardRef.current?.querySelectorAll('button:not(:disabled)') || [])];
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => {
      document.removeEventListener('keydown', trapFocus);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);
  return createPortal(
    <Modal open={open} onClose={onClose} title="Ownership structure" className="kycStructureModal" cardRef={cardRef}>
      <h2 className="kycStructureModalTitle">Ownership structure</h2>
      <p className="kycStructureModalHint">Choose the structure that best describes the client's ownership.</p>
      <OwnershipStructureChoice {...choiceProps} />
    </Modal>, document.body
  );
}
